import React, { useRef, useMemo } from 'react';
import {
  Animated,
  Text,
  ActivityIndicator,
  StyleSheet,
  View,
  TouchableOpacity,
} from 'react-native';
import { useAppTheme } from '../contexts/ThemeContext';

export default function Button({
  title,
  onPress,
  variant = 'primary',
  loading = false,
  disabled = false,
  size = 'md',
  icon = null,
  style,
}) {
  const { colors, spacing, radius, typography } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);
  const scale = useRef(new Animated.Value(1)).current;

  const isDisabled = disabled || loading;

  function handlePressIn() {
    Animated.spring(scale, {
      toValue: 0.965,
      useNativeDriver: true,
      tension: 300,
      friction: 10,
    }).start();
  }

  function handlePressOut() {
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
      tension: 300,
      friction: 10,
    }).start();
  }

  const containerStyle = [
    styles.base,
    styles[variant],
    styles[`size_${size}`],
    isDisabled && styles.disabled,
    style,
  ];

  const textStyle = [
    styles.text,
    styles[`text_${variant}`],
    styles[`textSize_${size}`],
  ];

  return (
    <TouchableOpacity
      onPress={isDisabled ? undefined : onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      activeOpacity={1}
      disabled={isDisabled}
    >
      <Animated.View style={[containerStyle, { transform: [{ scale }] }]}>
        {loading ? (
          <ActivityIndicator
            size="small"
            color={variant === 'primary' ? '#fff' : colors.accent}
          />
        ) : (
          <View style={styles.inner}>
            {icon && <View style={styles.iconWrapper}>{icon}</View>}
            <Text style={textStyle}>{title}</Text>
          </View>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    base: {
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: radius.md,
      flexDirection: 'row',
    },
    inner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconWrapper: {
      marginRight: spacing.sm,
    },

    // Variants
    primary: {
      backgroundColor: colors.accent,
      shadowColor: colors.accent,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.45,
      shadowRadius: 16,
      elevation: 8,
    },
    secondary: {
      backgroundColor: colors.surfaceElevated,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.18,
      shadowRadius: 6,
      elevation: 3,
    },
    ghost: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border,
    },
    danger: {
      backgroundColor: colors.danger,
      shadowColor: colors.danger,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.40,
      shadowRadius: 14,
      elevation: 7,
    },
    disabled: {
      opacity: 0.38,
    },

    // Sizes
    size_sm: {
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      minHeight: 36,
    },
    size_md: {
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.xl,
      minHeight: 50,
    },
    size_lg: {
      paddingVertical: spacing.base + 2,
      paddingHorizontal: spacing.xxl,
      minHeight: 58,
    },

    // Text
    text: {
      fontWeight: typography.weights.bold,
      letterSpacing: -0.2,
    },
    text_primary: {
      color: '#ffffff',
    },
    text_secondary: {
      color: colors.text,
    },
    text_ghost: {
      color: colors.textSecondary,
    },
    text_danger: {
      color: '#ffffff',
    },
    textSize_sm: {
      fontSize: typography.sizes.sm,
      letterSpacing: 0,
    },
    textSize_md: {
      fontSize: typography.sizes.base,
    },
    textSize_lg: {
      fontSize: typography.sizes.md,
      letterSpacing: -0.3,
    },
  });
}
